import { FailureRes } from "@smartrr/shared/req";
import { ERROR_TYPE } from "@smartrr/shared/types";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";

import { LoginError } from "../../types";

/**
 * Error types are added to error messages  by appending them to the end of the message with a '|' delimiter
 * e.g. `Invalid verification code.${errorTypeDelimiter + ERROR_TYPE.WRONG_CODE}`,
 * This isn't the best solution as should have its own key on the error response
 *
 * (Same for the initial minute of the error messages that require a count down timer, those should be their own keys and not selected from the message text)
 *
 * If we can get that update then we just need to update logic within getErrorTypeFromErrorMessage – the rest will remain the same
 */
export const getInitialMinute = (message: string) => {
  const minuteWithinErrorMessageText = message?.match(/\d+/)?.[0];
  return minuteWithinErrorMessageText ? parseInt(minuteWithinErrorMessageText) : 10;
};

export const getErrorTypeFromErrorMessage = (message: string) => {
  const splitErrorMessage = message.split("|");
  const errorType = splitErrorMessage[splitErrorMessage.length - 1].trim();
  return errorType as ERROR_TYPE;
};

export const transformErrorResponse = (res: FailureRes) => {
  const errorType = getErrorTypeFromErrorMessage(res.message);

  const logInErrorDefaults = {
    showTimer: false,
    initialMinute: 10,
  };

  let transformedResponse: LoginError;
  switch (errorType) {
    case ERROR_TYPE.NOT_FOUND_USER || ERROR_TYPE.WRONG_EMAIL: {
      transformedResponse = {
        ...res,
        ...logInErrorDefaults,
        errorType,
        localizedError: typedLocalize("passwordlessLogin.noAccountFound"),
      };
      break;
    }
    case ERROR_TYPE.TOO_MANY_ML:
    case ERROR_TYPE.TOO_MANY_OTP:
    case ERROR_TYPE.TOO_MANY_EMAIL: {
      transformedResponse = transformedResponse = {
        ...res,
        ...logInErrorDefaults,
        errorType,
        localizedError:
          typedLocalize("passwordlessLogin.tooManyPasswordlessError") +
          " " +
          typedLocalize("passwordlessLogin.tryAgain"),
        showTimer: true,
        initialMinute: getInitialMinute(res.message),
      };
      break;
    }
    case ERROR_TYPE.ACCOUNT_LOCKED: {
      transformedResponse = transformedResponse = {
        ...res,
        ...logInErrorDefaults,
        errorType,
        localizedError:
          typedLocalize("passwordlessLogin.accountLockedError") +
          " " +
          typedLocalize("passwordlessLogin.tryAgain"),
        showTimer: true,
        initialMinute: getInitialMinute(res.message),
      };
      break;
    }
    case ERROR_TYPE.NOT_ENABLED: {
      transformedResponse = {
        ...res,
        ...logInErrorDefaults,
        errorType,
        localizedError: typedLocalize("passwordlessLogin.notActivatedError"),
      };
      break;
    }
    case ERROR_TYPE.CANNOT_SEND_INVITE: {
      transformedResponse = {
        ...logInErrorDefaults,
        ...res,
        errorType,
        localizedError: typedLocalize("passwordlessLogin.notActivatedFailedInviteError"),
      };
      break;
    }
    case ERROR_TYPE.CODE_EXPIRED: {
      transformedResponse = {
        ...res,
        ...logInErrorDefaults,
        errorType,
        localizedError: typedLocalize("passwordlessLogin.codeExpiredError"),
      };
      break;
    }
    case ERROR_TYPE.CODE_USED: {
      transformedResponse = {
        ...res,
        ...logInErrorDefaults,
        errorType,
        localizedError: typedLocalize("passwordlessLogin.codeUsedError"),
      };
      break;
    }
    case ERROR_TYPE.WRONG_CODE: {
      transformedResponse = {
        ...res,
        ...logInErrorDefaults,
        errorType,
        localizedError: typedLocalize("passwordlessLogin.codeUsedError"),
      };
      break;
    }
    case ERROR_TYPE.SERVER_ERROR: {
      transformedResponse = {
        ...res,
        ...logInErrorDefaults,
        errorType,
        localizedError: typedLocalize("passwordlessLogin.serverErrorFallback"),
      };
      break;
    }
    default: {
      transformedResponse = {
        ...res,
        ...logInErrorDefaults,

        localizedError: typedLocalize("passwordlessLogin.serverErrorFallback"),
      };
    }
  }
  return transformedResponse;
};
